import * as React from 'react';

import CountryIcon from '~/UI/CountryIcon';
import { Country } from '~/Api/Country';
import DecimalInput from '~/UI/Input/DecimalInput';
import ERPPriceOriginDotMark from '../ERPPriceOriginDotMark';

import { IterventionTitles, InterventionType } from '../api/ERPInterventionCreateRequest';

import classNames from 'classnames';
import { formatNumericHumanDate } from '~/DateTime';
import { roundedHumanFormatWithoutCurrency } from '~/utils/Price/Price';

import type { ERPRequestApiResult } from '../api/ERPSessionRequest';
import type { InterventionTypeSerializable } from 'com.cogvio.pricemonitor.api';
import TypedSelect from '~/UI/Select/TypedSelect';

type CountryState = {
	type: InterventionTypeSerializable;
	month: string;
	price: {
		value: string;
		currency: string;
	} | null;
	error?: null | keyof CountryState;
};

export type InterventionState = {
	[countryCode: string]: CountryState[];
};

type InterventionValidationTorso = {
	type: InterventionTypeSerializable;
	price: CountryState['price'];
};

const isInterventionValid = (interventionTorso: InterventionValidationTorso) => {
	if (!interventionTorso.type) {
		return false;
	}

	if (interventionTorso.type === InterventionType['delist']) {
		return true;
	}

	return (
		!interventionTorso.price ||
		(Boolean(interventionTorso.price?.currency) &&
			Boolean(interventionTorso.price?.value) &&
			Number(interventionTorso.price?.value))
	);
};

const DEFAULT_PRICE = String(0);

const ERPInterventionModalRow = ({
	country,
	currency,
	erpDate,
	erpSessionResult,
	interventionState,
	months,
	onChange,
}: {
	country: Country;
	currency: string;
	erpDate: Date;
	erpSessionResult: ERPRequestApiResult;
	interventionState: InterventionState;
	months: moment.Moment[];
	onChange: React.Dispatch<React.SetStateAction<InterventionState>>;
}) => {
	const countryInterventions = interventionState[country.alpha2] || [];
	const usedUpMonths = new Set(countryInterventions.map(({ month }) => month));
	const availableMonth = months
		.find((momentDate) => !usedUpMonths.has(momentDate.format('YYYY-MM')))
		?.format('YYYY-MM');

	const inputPrice = React.useMemo(
		() => erpSessionResult.value?.erpSessionDetail.inputPrices.find((price) => price.country.alpha2 === country.alpha2),
		[country, erpSessionResult]
	);

	const hasPrice = typeof inputPrice?.price.price.value !== 'undefined';

	return (
		<>
			<tr className={classNames({ 'bg-gray-100': countryInterventions.length > 0 })}>
				<td className='pl-3 pr-2 py-1 font-display text-label font-semibold text-gray-800 leading-none border-b border-gray-200 w-40'>
					<div className='flex items-center'>
						<CountryIcon code={country.alpha2} name={country.name} />
						&nbsp;
						<div className=''>{country.name}</div>
					</div>
				</td>
				<td className='pl-2 pt-1 pb-1.5 border-b border-r border-gray-200'>
					<div className='flex justify-start items-center'>
						<div className='grow-0 shrink-0 pr-2'>
							<button
								className='flex items-center -ml-px font-display text-labelsmall font-semibold text-gray-500 leading-none transtion transition-all hover:text-black whitespace-nowrap'
								disabled={!availableMonth}
								type='button'
								onClick={() => {
									if (!availableMonth) {
										return;
									}

									const defaultPrice: CountryState['price'] = {
										value: DEFAULT_PRICE,
										currency,
									};
									const type = InterventionType['price-change'];

									onChange((prev) => ({
										...prev,
										[country.alpha2]: (prev[country.alpha2] || []).concat([
											{
												type,
												month: availableMonth,
												price: defaultPrice,
												error: isInterventionValid({ type, price: defaultPrice }) ? null : 'price',
											},
										]),
									}));
								}}
							>
								<svg
									className='relative -mt-px h-5 w-5 mr-0.5'
									fill='none'
									stroke='currentColor'
									strokeWidth='1.5'
									viewBox='0 0 24 24'
									xmlns='http://www.w3.org/2000/svg'
								>
									<path
										d='M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z'
										strokeLinecap='round'
										strokeLinejoin='round'
									></path>
								</svg>
								<span className='inline-block pt-0.5'>Add intervention</span>
							</button>
						</div>
					</div>
				</td>
				<td className='px-2 py-1.5 border-b border-gray-200 w-40'>
					<div className='flex items-center justify-between_'>
						<ERPPriceOriginDotMark
							price={inputPrice?.price.price.value}
							userDefined={Boolean(inputPrice?.isUserDefined)}
							tooltipSide='left'
						/>
						<span className='font-roboto text-label font-semibold text-gray-500 leading-none tabular-nums whitespace-nowrap'>
							{hasPrice ? roundedHumanFormatWithoutCurrency(String(inputPrice?.price.price.value || '')) : ''}
						</span>
					</div>
				</td>
				<td className='pr-3 pl-2 py-1 font-roboto text-label font-semibold text-gray-500 leading-none border-b border-gray-200 whitespace-nowrap text-right tabular-nums w-28'>
					{formatNumericHumanDate(erpDate)}
				</td>
			</tr>
			{countryInterventions.map((intervention, index) => (
				<tr key={`${index}`}>
					<td className='pl-3 py-0.5 border-b border-gray-200'>
						<div className='flex items-center justify-end'>
							<button
								className='flex items-center font-display text-labelsmall font-semibold text-gray-300 leading-none transtion transition-all hover:text-black'
								type='button'
								onClick={() =>
									onChange((prev) => {
										const origArray = prev[country.alpha2] || [];
										origArray.splice(index, 1);
										return {
											...prev,
											[country.alpha2]: Array.from(origArray),
										};
									})
								}
							>
								<svg
									className='w-5 h-5 mr-1'
									fill='none'
									stroke='currentColor'
									strokeWidth='1.5'
									viewBox='0 0 24 24'
									xmlns='http://www.w3.org/2000/svg'
								>
									<path
										d='M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
										strokeLinecap='round'
										strokeLinejoin='round'
									></path>
								</svg>
							</button>
						</div>
					</td>
					<td className='border-b border-r border-gray-200 bg-gray-100'>
						<div className='pl-7 pr-2 w-full'>
							<TypedSelect
								className='w-full h-6 px-1 border border-gray-300 rounded-sm font-roboto text-labelsmall font-semibold leading-none text-cPurple-600 truncate bg-white cursor-pointer align-top'
								options={(Object.keys(InterventionType) as InterventionTypeSerializable[]).map((type) => ({
									value: type,
									title: IterventionTitles.get(type),
								}))}
								value={intervention.type}
								onTypedValueChange={(value) => {
									onChange((prev) => ({
										...prev,
										[country.alpha2]: (prev[country.alpha2] || []).map((candidate, candidateIndex) =>
											candidateIndex === index
												? {
														...candidate,
														type: value,
														price: null,
														error: isInterventionValid({ type: value, price: null }) ? null : 'type',
												  }
												: candidate
										),
									}));
								}}
							/>
						</div>
					</td>
					<td className='pl-2 pr-1 py-1 border-b border-gray-200 w-28 bg-gray-100'>
						{intervention.type === InterventionType['price-change'] && (
							<DecimalInput
								allowEmpty
								autoFocus
								selectOnFocus
								className='w-full h-6 px-1.5 font-roboto text-label text-cPurple-600 font-semibold leading-4 border border-gray-300 rounded-sm tabular-nums bg-white align-top'
								hasError={intervention.error === 'price'}
								type='text'
								value={intervention.price?.value || ''}
								onDecimalInput={(value) => {
									onChange((prev) => ({
										...prev,
										[country.alpha2]: (prev[country.alpha2] || []).map((candidate, candidateIndex) =>
											candidateIndex === index
												? {
														...candidate,
														price: typeof value !== 'undefined' ? { value, currency } : null,
														error: isInterventionValid({ ...candidate, price: { value, currency } }) ? null : 'price',
												  }
												: candidate
										),
									}));
								}}
							/>
						)}
					</td>
					<td className='pr-3 pl-1  py-1 border-b border-gray-200 whitespace-nowrap w-28 text-right bg-gray-100'>
						<TypedSelect
							className='w-full h-6 px-1 border border-gray-300 rounded-sm font-roboto text-label font-semibold leading-6 text-cPurple-600 truncate bg-white cursor-pointer align-top text-right'
							options={months.map((momentDate) => {
								const formatedValue = momentDate.format('YYYY-MM');

								return {
									disabled: usedUpMonths.has(formatedValue) && formatedValue !== intervention.month,
									value: formatedValue,
									title: formatedValue,
								};
							})}
							value={intervention.month}
							onTypedValueChange={(value) => {
								onChange((prev) => ({
									...prev,
									[country.alpha2]: (prev[country.alpha2] || []).map((candidate, candidateIndex) =>
										candidateIndex === index ? { ...candidate, month: value } : candidate
									),
								}));
							}}
						/>
					</td>
				</tr>
			))}
		</>
	);
};

export default React.memo(ERPInterventionModalRow);
